import { DatePickerSelectedValue } from '@storeblocks/datepicker';
import { format } from 'date-fns';
import { type FC } from 'react';
import styled from 'styled-components';

import { Show } from '@/components/Show';
import { DateSearch } from '@/components/Toolbar/DateSearch';
import { DownloadButton } from '@/components/Toolbar/DownloadButton';
import { DownloadErrorAlert } from '@/components/Toolbar/DownloadErrorAlert';
import { useSearchParams } from '@/components/Toolbar/hooks/useSearchParams';
import { TextSearch } from '@/components/Toolbar/TextSearch';
import { useHandleAsyncOperation } from '@/hooks/useHandleAsyncError';
import { useTexts } from '@/hooks/useTexts';
import { media } from '@/styles/variables';

export interface SearchParams {
  dateISO?: string;
  query?: string;
}

interface Props {
  numberOfFilesToDownload: number;
  onChange: (params: SearchParams) => void;
  onDownloadClick: () => Promise<void>;
}

export const Toolbar: FC<Props> = ({
  onChange: onSearchParamsChanged,
  onDownloadClick,
  numberOfFilesToDownload,
}: Props) => {
  const texts = useTexts();

  const [handleDownloadClick, , downloadError] =
    useHandleAsyncOperation(onDownloadClick);

  const searchParams = useSearchParams<SearchParams>(onSearchParamsChanged);

  const updateTextSearch = (query: string): void => {
    searchParams.update((state) => {
      state.query = query;
    });
  };

  const updateDateSearch = ({ date }: DatePickerSelectedValue): void => {
    const dateString = date ? format(date, 'yyyy-MM-dd') : '';

    searchParams.update((state) => {
      state.dateISO = dateString;
    });
  };

  return (
    <>
      <Wrapper>
        <TextSearch
          onChange={updateTextSearch}
          label={texts.trading.history.contractNotes.toolbar.search}
        />
        <DateSearch
          onChange={updateDateSearch}
          label={texts.trading.history.contractNotes.toolbar.orderDate}
        />
        <DownloadButton
          onClick={handleDownloadClick}
          numberOfFilesToDownload={numberOfFilesToDownload}
        />
      </Wrapper>

      <Show when={downloadError}>
        <DownloadErrorAlert />
      </Show>
    </>
  );
};

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  // Rows containing input fields must have a minimum height of 110px to allow room for an error alert
  // to be displayed below the input fields.
  grid-template-rows: minmax(110px, auto);
  gap: 16px;

  & > button {
    // Align the download button with the input fields
    margin-top: 30px;
  }

  @media ${media.tabletAndBelow} {
    grid-template-columns: 1fr;
    // Rows containing input fields must have a minimum height of 110px to allow room for an error alert
    // to be displayed below the input fields.
    grid-template-rows: repeat(2, minmax(110px, auto)) auto;

    & > button {
      margin-top: 0;
      justify-self: start;
    }
  }
`;
